import { Select, SelectItem } from '@utilitywarehouse/hearth-react';
import { FormikProps, getIn, useField, useFormikContext } from 'formik';

import { SelectOptions } from '@src/types/index';
import { validatePostcode } from '@utils/validators';

import { FieldProps, NestedFormValues } from '../types';
import { getFieldName } from '../utils';

interface ResultsFieldProps<T> extends Omit<FieldProps, 'onChange'> {
  options: SelectOptions;
  loading: boolean;
  error: boolean;
  formName: T;
  onChange: (value: string) => void;
  dataTestId?: string;
}

const ResultsField = <T extends string>({
  id,
  error,
  label,
  name,
  onChange,
  options,
  value = '',
  disabled,
  formName,
  dataTestId,
}: ResultsFieldProps<T>) => {
  const { touched, values }: FormikProps<NestedFormValues<T>> =
    useFormikContext();
  const [field] = useField(getFieldName(formName, name));
  const hasPostcode = validatePostcode(
    getIn(values, `${formName}.postcode`, null)
  );

  if (!hasPostcode && !getIn(touched, `${formName}.postcode`, null)) {
    return null;
  }

  return (
    <div className="addressPicker__resultsField">
      <Select
        {...field}
        id={id}
        label={label}
        value={value}
        onValueChange={onChange}
        validationStatus={error ? 'invalid' : 'valid'}
        validationText={error ? 'Failed to fetch addresses' : undefined}
        disabled={disabled}
        data-testid={dataTestId}
        required
      >
        {options.length === 0 && (
          <SelectItem value="empty" disabled>
            No addresses found
          </SelectItem>
        )}
        {options.length > 0 &&
          options.map((option, index) => (
            <SelectItem
              value={option.value}
              key={option.value}
              data-testid={`address-picker-select-option-${index + 1}`}
            >
              {option.label}
            </SelectItem>
          ))}
      </Select>
    </div>
  );
};

export { ResultsField };
